import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { api } from "./api";

const loaded = new Set();
let registry = [];

const ALIASES = {
  "zh-tw": "zh-Hant", "zh-hk": "zh-Hant", "zh-mo": "zh-Hant", "zh-hant": "zh-Hant",
  "zh-cn": "zh-Hans", "zh-sg": "zh-Hans", "zh-hans": "zh-Hans", zh: "zh-Hans",
  nb: "no", nn: "no", tl: "fil", iw: "he", in: "id",
};

function normalize(tag) {
  const low = (tag || "").toLowerCase();
  if (ALIASES[low]) return ALIASES[low];
  const base = low.split("-")[0];
  return ALIASES[base] || base;
}

export function detectLanguage(enabledCodes, fallback) {
  const stored = localStorage.getItem("lang");
  if (stored && enabledCodes.includes(stored)) return stored;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const p of prefs) {
    const code = normalize(p);
    if (enabledCodes.includes(code)) return code;
  }
  if (enabledCodes.includes(fallback)) return fallback;
  return enabledCodes[0] || "en";
}

async function loadBundle(lng) {
  if (loaded.has(lng)) return;
  const { data } = await api.get(`/i18n/${lng}`);
  i18n.addResourceBundle(lng, "translation", data, true, true);
  loaded.add(lng);
}

export function applyDocumentLanguage(lng) {
  const meta = registry.find((l) => l.code === lng);
  document.documentElement.lang = lng;
  document.documentElement.dir = meta?.dir === "rtl" ? "rtl" : "ltr";
}

let initPromise = null;

// Memoized: React StrictMode runs effects twice; a second i18n.init({resources:{}}) wiped loaded bundles (raw keys in UI).
export function initI18n(languages, defaultLang) {
  if (!initPromise) initPromise = doInitI18n(languages, defaultLang);
  return initPromise;
}

async function doInitI18n(languages, defaultLang) {
  registry = languages;
  const lng = detectLanguage(languages.map((l) => l.code), defaultLang);
  await i18n.use(initReactI18next).init({
    lng,
    fallbackLng: "en",
    resources: {},
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  });
  await Promise.all([loadBundle("en"), loadBundle(lng)]);
  await i18n.changeLanguage(lng);
  applyDocumentLanguage(lng);
  return lng;
}

export async function changeLanguage(lng) {
  await loadBundle(lng);
  await i18n.changeLanguage(lng);
  localStorage.setItem("lang", lng);
  applyDocumentLanguage(lng);
}

export async function reloadBundle(lng) {
  loaded.delete(lng);
  await loadBundle(lng);
}

export function isRtl(lng) {
  return registry.find((l) => l.code === lng)?.dir === "rtl";
}

export default i18n;
